"use client";

import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Linkedin,
} from "lucide-react";
import logo from "@/imports/Rann-Utsav-Logo-scaled_cr.png";
import Link from "next/link";
import Image from "next/image";
import { useAppSelector } from "@/hooks/hooks";
import { ISettings } from "@/types/home.types";
import React, { useEffect, useState, useMemo } from "react";
import LinksSection from "./LinksSection";
import { createLookupObject } from "@/utils/helper";
import VisitCounter from "./VisitCounter";

export default function Footer() {
  const { SettingList, PageDetails, HeaderMenuList } = useAppSelector(
    (state) => state.home,
  );
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, any>>({});

  useEffect(() => {
    if (PageDetails && PageDetails.length > 0) {
      setLookUpCMSData(createLookupObject(PageDetails, "Name"));
    }
  }, [PageDetails]);

  const Settings: Record<string, ISettings> = useMemo(() => {
    return (
      SettingList?.reduce(
        (acc, item) => {
          acc[item?.SettingKey] = item;
          return acc;
        },
        {} as Record<string, ISettings>,
      ) || {}
    );
  }, [SettingList]);

  const PhoneNo = Settings?.["PhoneNo"]?.SettingValue;
  const PhoneNo2 = Settings?.["PhoneNo2"]?.SettingValue;
  const PhoneNo3 = Settings?.["PhoneNo3"]?.SettingValue;

  const EmailID = Settings?.["EmailID"]?.SettingValue;
  const EmailID2 = Settings?.["EmailID2"]?.SettingValue;
  const EmailID3 = Settings?.["EmailID3"]?.SettingValue;

  const Address = Settings?.["Address"]?.SettingValue;
  const Address2 = Settings?.["Address2"]?.SettingValue;
  const Address3 = Settings?.["Address3"]?.SettingValue;

  const AddressMap = Settings?.["AddressMap"]?.SettingValue;
  const AddressMap2 = Settings?.["AddressMap2"]?.SettingValue;
  const AddressMap3 = Settings?.["AddressMap3"]?.SettingValue;

  const AddressIframe = Settings?.["AddressIframe"]?.SettingValue;

  const FacebookLink = Settings?.["FacebookLink"]?.SettingValue;
  const InstaLink = Settings?.["InstaLink"]?.SettingValue;
  const XLink = Settings?.["XLink"]?.SettingValue;
  const LinkedInLink = Settings?.["LinkedInLink"]?.SettingValue;
  const YoutubeLink = Settings?.["YoutubeLink"]?.SettingValue;

  const mapContent = useMemo(() => {
    if (!AddressIframe) return { __html: "" };
    let updatedIframe = AddressIframe;
    if (!updatedIframe.includes('loading="')) {
      updatedIframe = updatedIframe.replace("<iframe", '<iframe loading="lazy"');
    }
    return { __html: updatedIframe };
  }, [AddressIframe]);

  const SocialLinks = useMemo(() => [
    { Icon: Facebook, link: FacebookLink, name: "Facebook" },
    { Icon: Instagram, link: InstaLink, name: "Instagram" },
    { Icon: Twitter, link: XLink, name: "Twitter" },
    { Icon: Linkedin, link: LinkedInLink, name: "Linked In" },
    { Icon: Youtube, link: YoutubeLink, name: "Youtube" },
  ], [FacebookLink, InstaLink, XLink, LinkedInLink, YoutubeLink]);

  return (
    <>
      {lookUpCMSData?.["Footer Links"] && (
        <LinksSection cmsData={lookUpCMSData?.["Footer Links"]} />
      )}
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-18 gap-8">
            {/* About */}
            <div className="lg:col-span-4 col-span-1">
              <Image
                src={logo}
                alt="Rann Utsav"
                width={245}
                height={48}
                className="h-12 w-auto mb-4 cursor-pointer brightness-115"
              />
              <p className="text-gray-400 mb-4">
                Experience the magic of India's white desert with our
                ultra-luxury packages. Official authorized tour operator for
                Rann Utsav.
              </p>
              <div className="flex gap-4">
                {SocialLinks?.filter((x) => x.link).map(
                  ({ Icon, link, name }, i) => (
                    <Link
                      key={i}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Visit our ${name} page`}
                      className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-pink-600 transition"
                    >
                      <Icon className="w-5 h-5" />
                    </Link>
                  ),
                )}
                {/* <Link
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-pink-600 transition"
              >
                <Facebook className="w-5 h-5" />
              </Link>
              <Link
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-pink-600 transition"
              >
                <Instagram className="w-5 h-5" />
              </Link>
              <Link
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-pink-600 transition"
              >
                <Twitter className="w-5 h-5" />
              </Link>
              <Link
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-pink-600 transition"
              >
                <Youtube className="w-5 h-5" />
              </Link> */}
              </div>
            </div>

            {/* Quick Links */}
            <div className="lg:col-span-2 col-span-1">
              <h3 className="text-xl mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link href="/" className="hover:text-pink-400 transition">
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    href="/special-dates"
                    className="hover:text-pink-400 transition"
                  >
                    Special Dates
                  </Link>
                </li>
                <li>
                  <Link
                    href="/gallery"
                    className="hover:text-pink-400 transition"
                  >
                    Gallery
                  </Link>
                </li>
                {/* <li>
                  <Link
                    href="/hotels"
                    className="hover:text-pink-400 transition"
                  >
                    Hotels
                  </Link>
                </li> */}
                <li>
                  <Link
                    href="/about-us"
                    className="hover:text-pink-400 transition"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    href="/contact"
                    className="hover:text-pink-400 transition"
                  >
                    Contact
                  </Link>
                </li>
                {/* <li>
                  <Link href="/blog" className="hover:text-pink-400 transition">
                    Blogs
                  </Link>
                </li> */}
              </ul>
            </div>

            {/* Dhordo Resorts */}
            <div className="lg:col-span-4 col-span-1">
              <h3 className="text-xl mb-4">Dhordo Tent City</h3>
              <ul className="space-y-2 text-gray-400">
                {(HeaderMenuList?.DhordoTentCity || []).map((item, idx) => (
                  <li key={idx}>
                    <Link
                      href={`/resort/${item.URL}`}
                      className="hover:text-pink-400 transition"
                    >
                      {item.Title}
                    </Link>
                  </li>
                ))}
              </ul>
              <h3 className="text-xl mt-6 mb-4">Dholavira Tent City</h3>
              <ul className="space-y-2 text-gray-400">
                {(HeaderMenuList?.DholaviraTentCity || []).map((item, idx) => (
                  <li key={idx}>
                    <Link
                      href={`/resort/${item.URL}`}
                      className="hover:text-pink-400 transition"
                    >
                      {item.Title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="lg:col-span-4 col-span-1">
              <h3 className="text-xl mb-4">Contact Us</h3>
              <ul className="space-y-4 text-gray-400">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 mt-1 flex-shrink-0 text-pink-500" />
                  <div className="flex flex-col gap-2">
                    {Address && (
                      <Link href={AddressMap || "#"}>
                        {Address}
                        <br />
                      </Link>
                    )}
                    {Address2 && (
                      <Link href={AddressMap2 || "#"}>
                        {Address2}
                        <br />
                      </Link>
                    )}
                    {Address3 && (
                      <Link href={AddressMap3 || "#"}>
                        {Address3}
                        <br />
                      </Link>
                    )}
                    {/* <span>
                  B-701/702, PNTC, Radio Mirchi Road, Satellite, Ahmedabad
                </span> */}
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-5 h-5 mt-1 flex-shrink-0 text-pink-500" />
                  <div className="flex flex-col gap-2">
                    {PhoneNo && (
                      <p>
                        <Link
                          href={`tel:${PhoneNo}`}
                          className="hover:text-pink-400 transition"
                        >
                          {PhoneNo || "+91 91057 00099"}
                        </Link>
                      </p>
                    )}
                    {PhoneNo2 && (
                      <p>
                        <Link
                          href={`tel:${PhoneNo2}`}
                          className="hover:text-pink-400 transition"
                        >
                          {PhoneNo2}
                        </Link>
                      </p>
                    )}
                    {PhoneNo3 && (
                      <p>
                        <Link
                          href={`tel:${PhoneNo3}`}
                          className="hover:text-pink-400 transition"
                        >
                          {PhoneNo3}
                        </Link>
                      </p>
                    )}
                  </div>
                  {/* <Link
                  href="tel:+919099667119"
                  className="hover:text-pink-400 transition"
                >
                  +91 9099667119
                </Link> */}
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-5 h-5 mt-1 flex-shrink-0 text-pink-500" />
                  <div className="flex flex-col gap-2">
                    {EmailID && (
                      <p>
                        <Link
                          href={`mailto:${EmailID}`}
                          target="_blank"
                          className="hover:text-pink-400 transition"
                        >
                          {EmailID}
                        </Link>
                      </p>
                    )}
                    {EmailID2 && (
                      <p>
                        <Link
                          href={`mailto:${EmailID2}`}
                          target="_blank"
                          className="hover:text-pink-400 transition"
                        >
                          {EmailID2}
                        </Link>
                      </p>
                    )}
                    {EmailID3 && (
                      <p>
                        <Link
                          href={`mailto:${EmailID3}`}
                          target="_blank"
                          className="hover:text-pink-400 transition"
                        >
                          {EmailID3}
                        </Link>
                      </p>
                    )}
                  </div>
                  {/* <Link
                  href="mailto:booking@aalpine.in"
                  className="hover:text-pink-400 transition"
                >
                  booking@aalpine.in
                </Link> */}
                </li>
              </ul>
            </div>

            {/* Google Map Embed - Container Width with Rounded Borders */}
            {AddressIframe && (
              <div
                className="col-span-1 md:col-span-2 lg:col-span-4 rounded-xl overflow-hidden h-[200px] w-full [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:border-0"
                dangerouslySetInnerHTML={mapContent}
              />
            )}
          </div>
          {/* Bottom Bar */}
          <div className="my-6">
            <VisitCounter />
          </div>
          <div className="pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-gray-400 text-sm">
              <p className="text-center md:text-left">
                &copy; {new Date().getFullYear()} Rann Utsav Ultra Luxury. All
                rights reserved.
              </p>
              <div className="flex gap-6 text-sm justify-center md:justify-end">
                <a
                  className="text-white/60 transition-colors"
                  target="_blank"
                  href="https://aseuminfotech.com/"
                >
                  Developed by{" "}
                  <span className="text-white hover:text-pink-400 transition-colors">
                    ASEUM INFOTECH
                  </span>
                </a>
              </div>
              <div className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-2">
                <Link
                  href="/privacy-policy"
                  className="hover:text-pink-400 transition"
                >
                  Privacy Policy
                </Link>
                <Link
                  href="/terms-and-conditions"
                  className="hover:text-pink-400 transition"
                >
                  Terms & Conditions
                </Link>
                <Link
                  href="/cancellation-policy"
                  className="hover:text-pink-400 transition"
                >
                  Cancellation Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
